import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/browser'
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'
import config from '../config'

type User = {
  id: number
  name: string
  email: string
}

type AmplitudeContextType = {
  isEnabled: boolean
  track: (eventName: string, eventProperties?: Record<string, any>) => void
}

function isUser(data: unknown): data is User {
  if (typeof data !== 'object' || data === null) {
    return false
  }

  if ('id' in data && 'name' in data && 'email' in data) {
    return [data.id, data.name, data.email].every(field => typeof field === 'string' || typeof field === 'number')
  }

  return false
}

const apiKey = import.meta.env?.VITE_AMPLITUDE_KEY

const AmplitudeContext = createContext<AmplitudeContextType>({
  isEnabled: false,
  track: () => {}
})

export function AmplitudeProvider({ children }: PropsWithChildren<unknown>) {
  const [isEnabled, setIsEnabled] = useState(false)
  const initializedRef = useRef(false)

  useEffect(function initializeAmplitude() {
    if (initializedRef.current) {
      return
    }

    initializedRef.current = true

    if (!config.PROD && !config.STAGING) {
      // eslint-disable-next-line no-console
      console.warn('Amplitude is disabled in development environment')
      return
    }

    if (typeof apiKey !== 'string' || apiKey.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('Amplitude API key is not set. Please provide a valid API key to enable analytics.')
      return
    }

    amplitude
      .init(apiKey, {
        minIdLength: 1,
        autocapture: {
          attribution: false,
          fileDownloads: false,
          formInteractions: false,
          pageViews: false,
          sessions: false,
          elementInteractions: false,
          networkTracking: false,
          webVitals: false,
          frustrationInteractions: false
        }
      })
      .promise.then(() => {
        if (isUser(window.user)) {
          // eslint-disable-next-line no-console
          console.log('Amplitude initialized with user:', window.user.id)

          amplitude.setIdentity({
            userId: window.user.id.toString(),
            userProperties: {
              name: window.user.name,
              email: window.user.email,
              version: import.meta.env.VITE_SOURCE_VERSION
            }
          })

          setIsEnabled(true)
          return
        }

        // eslint-disable-next-line no-console
        console.warn('User data is not available or invalid, Amplitude initialized without user identity')
      })
  }, [])

  const track = useCallback(
    (eventName: string, eventProperties?: Record<string, any>) => {
      if (!isEnabled) {
        // eslint-disable-next-line no-console
        console.warn(`Attempted to log event "${eventName}" but Amplitude is not enabled.`)
        return
      }

      amplitude.track(eventName, eventProperties).promise.catch(err => Sentry.captureException(err))
    },
    [isEnabled]
  )

  return <AmplitudeContext.Provider value={{ isEnabled, track }}>{children}</AmplitudeContext.Provider>
}

export const useAmplitude = () => useContext(AmplitudeContext)
